import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Avatar, ScorePill, IntentBadge } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LoadingSkeleton } from "@/components/shared/States";
import { Mail, Phone, Calendar, Plus, FileText, Sparkles, ArrowLeft, Loader2, Zap, TrendingUp } from "lucide-react";
import { timeAgo } from "@/lib/format";
import { toast } from "sonner";

export default function Contact360() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [activities, setActivities] = useState([]);
  const [scoring, setScoring] = useState(false);
  const [intel, setIntel] = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [note, setNote] = useState("");

  const load = async () => {
    const { data } = await api.get(`/contacts/${id}`); setC(data);
    const a = await api.get(`/activities?record_id=${id}`); setActivities(a.data.items);
  };
  useEffect(() => { load(); }, [id]);

  const score = async () => { setScoring(true); try { const { data } = await api.post(`/contacts/${id}/score`); toast.success("Lead re-scored"); await load(); } catch { toast.error("Scoring failed"); } finally { setScoring(false); } };
  const runIntel = async () => { setIntelLoading(true); try { const { data } = await api.post(`/contacts/${id}/ai-intel`); setIntel(data); } catch { toast.error("AI intel failed"); } finally { setIntelLoading(false); } };
  const addNote = async () => { if (!note.trim()) return; await api.post("/notes", { record_id: id, record_type: "contact", content: note }); setNote(""); toast.success("Note added"); load(); };

  if (!c) return <LoadingSkeleton />;
  const aiScore = c.ai_score;

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={() => navigate("/app/contacts")} className="mb-3 text-slate-500"><ArrowLeft className="h-4 w-4 mr-1" />Contacts</Button>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Header */}
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <div className="flex items-start gap-4">
              <Avatar name={`${c.first_name} ${c.last_name}`} src={c.avatar} size={64} />
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-slate-900">{c.first_name} {c.last_name}</h1>
                <p className="text-sm text-slate-500">{c.title} · {c.company_name}</p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="text-xs text-slate-500">Lead Score</span><ScorePill score={c.lead_score} />
                  <span className="text-xs text-slate-500 ml-2">Intent</span><IntentBadge value={c.buying_intent} />
                  <span className="text-xs text-slate-500 ml-2">Engagement</span><span className="text-xs font-semibold">{c.engagement_score}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button size="sm" variant="outline"><Mail className="h-4 w-4 mr-1" />Email</Button>
              <Button size="sm" variant="outline"><Phone className="h-4 w-4 mr-1" />Call</Button>
              <Button size="sm" variant="outline"><Calendar className="h-4 w-4 mr-1" />Meeting</Button>
              <Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-1" />Task</Button>
              <Button size="sm" data-testid="rescore-contact" className="aurelo-gradient text-white" onClick={score} disabled={scoring}>{scoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Zap className="h-4 w-4 mr-1" />AI Score</>}</Button>
            </div>
          </div>

          <Tabs defaultValue="activity">
            <TabsList><TabsTrigger value="activity">Activity</TabsTrigger><TabsTrigger value="details">Details</TabsTrigger><TabsTrigger value="notes">Notes</TabsTrigger></TabsList>
            <TabsContent value="activity" className="mt-3">
              <div className="rounded-2xl bg-white border border-slate-200 p-5">
                {activities.length === 0 ? <div className="text-sm text-slate-400 py-6 text-center">No activity yet.</div> : (
                  <div className="relative pl-5">
                    <div className="absolute left-1.5 top-1 bottom-1 w-px bg-slate-200" />
                    {activities.map((a) => (
                      <div key={a.id} className="relative pb-4">
                        <div className="absolute -left-[13px] top-1 h-2.5 w-2.5 rounded-full aurelo-gradient ring-2 ring-white" />
                        <div className="text-sm text-slate-700">{a.description}</div>
                        <div className="text-xs text-slate-400">{timeAgo(a.created_at)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
            <TabsContent value="details" className="mt-3">
              <div className="rounded-2xl bg-white border border-slate-200 p-5 grid grid-cols-2 gap-4 text-sm">
                {[["Email", c.email], ["Phone", c.phone], ["Title", c.title], ["Company", c.company_name], ["Source", c.source], ["Lifecycle", c.lifecycle_stage], ["Owner", c.owner_name], ["Location", c.location]].map(([l, v]) => (
                  <div key={l}><div className="text-xs text-slate-400">{l}</div><div className="font-medium text-slate-800">{v || "—"}</div></div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="notes" className="mt-3">
              <div className="rounded-2xl bg-white border border-slate-200 p-5">
                <div className="flex gap-2"><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30" /><Button onClick={addNote}>Add</Button></div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* AI panel */}
        <div className="space-y-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-[#7C3AED] flex items-center gap-1.5 mb-3"><Sparkles className="h-4 w-4" />AI Intelligence</h3>
            {aiScore ? (
              <div className="space-y-2.5">
                {[["Fit", aiScore.fit_score], ["Behavioral", aiScore.behavioral_score], ["Engagement", aiScore.engagement_score], ["Intent", aiScore.intent_score]].map(([l, v]) => (
                  <div key={l}><div className="flex justify-between text-xs mb-1"><span className="text-slate-500">{l} Score</span><span className="font-semibold tabular-nums">{v}</span></div><div className="h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full aurelo-gradient" style={{ width: `${v}%` }} /></div></div>
                ))}
                <div className="rounded-xl bg-slate-50 p-3 mt-3"><div className="flex items-center justify-between"><span className="text-xs text-slate-500">Conversion Probability</span><span className="text-lg font-bold text-[#7C3AED]">{aiScore.conversion_probability}%</span></div><div className="text-xs text-slate-500 mt-1">Decision window ~{aiScore.decision_window_days} days</div></div>
                <p className="text-xs text-slate-500 mt-2">{aiScore.rationale}</p>
              </div>
            ) : (
              <div className="text-center py-4"><p className="text-sm text-slate-500 mb-3">Run AI scoring to see fit, intent & conversion probability.</p><Button size="sm" className="aurelo-gradient text-white" onClick={score} disabled={scoring}>{scoring ? <Loader2 className="h-4 w-4 animate-spin" /> : "Analyze"}</Button></div>
            )}
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2"><h3 className="text-sm font-semibold flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-[#3B82F6]" />Relationship Brief</h3></div>
            {intel ? (
              <div className="space-y-2 text-sm"><p className="text-slate-600 text-xs">{intel.overview}</p>{intel.suggested_strategy && <div className="rounded-xl bg-violet-50 p-3 text-xs text-violet-800"><b>Next action:</b> {intel.suggested_strategy}</div>}</div>
            ) : (
              <Button size="sm" variant="outline" className="w-full" onClick={runIntel} disabled={intelLoading}>{intelLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate AI Brief"}</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
